/* Sticky controls delegate to the original picker and product form. */
if (!customElements.get('ayaq-pdp-purchase-bar')) {
  customElements.define(
    'ayaq-pdp-purchase-bar',
    class extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.ayaq-pdp');
        this.button = this.section?.querySelector('product-form [name="add"]');
        if (!this.button || this.controller) return;
        this.controller = new window.AbortController();
        const { signal } = this.controller;
        this.picker = this.section.querySelector(
          'variant-radios, variant-selects',
        );
        this.sizeGroup = this.section.querySelector(
          '.ayaq-pdp__option-group--size',
        );
        this.price = this.section.querySelector('[id^="price-"]');
        this.error = this.section.querySelector(
          '.product-form__error-message-wrapper',
        );
        this.submit = this.querySelector('[data-pdp-purchase-submit]');
        this.content = this.querySelector('.ayaq-pdp__purchase-content');
        this.productTitle = this.querySelector('.ayaq-pdp__purchase-title');
        this.actions = this.querySelector('.ayaq-pdp__purchase-actions');
        this.mobile = window.matchMedia('(max-width: 989px)');
        this.groups = Array.from(
          this.picker?.querySelectorAll('fieldset, select') || [],
        );
        const options = this.querySelector('[data-pdp-purchase-options]');
        options.replaceChildren();
        this.colorOptions = this.querySelector('[data-pdp-purchase-colors]');
        this.colorOptions.replaceChildren();
        this.priceLabel = this.querySelector('[data-pdp-purchase-price]');
        this.rows = [];
        this.layoutMoves = [
          ['[data-ayaq-size-guide-trigger]', '[data-pdp-guide]'],
        ]
          .map(([source, destination]) => {
            const element = this.section.querySelector(source);
            const slot = this.section.querySelector(destination);
            if (!element || !slot) return null;
            const marker = document.createComment('PDP desktop position');
            element.before(marker);
            return { element, slot, marker };
          })
          .filter(Boolean);
        this.choices = this.groups.map((group, index) => {
          const row = document.createElement('div');
          row.className = 'ayaq-pdp__purchase-group';
          if (group.closest('.ayaq-pdp__option-group--size')) {
            row.classList.add('ayaq-pdp__purchase-group--size');
          }
          row.setAttribute('role', 'group');
          const sourceLabel =
            group.querySelector('[data-pdp-option-label]') ||
            group.querySelector('legend') ||
            this.picker.querySelector(`label[for="${group.id}"]`);
          row.setAttribute(
            'aria-label',
            sourceLabel?.textContent.trim().split(':')[0] || group.name,
          );
          const isColor =
            group.classList.contains('ayaq-pdp__color-options') ||
            Boolean(group.querySelector('label img'));
          (isColor ? this.colorOptions : options).append(row);
          this.rows.push(row);
          return Array.from(
            group.querySelectorAll('input[type="radio"], option'),
          ).map((control) => {
            const label = control.labels?.[0];
            const notify = control
              .closest('.ayaq-pdp__size-choice')
              ?.querySelector('[data-pdp-restock-size]');
            const choice = document.createElement('button');
            choice.type = 'button';
            choice.className = 'ayaq-pdp__purchase-choice';
            choice.dataset.pdpOption = String(index);
            choice.dataset.value = control.value;
            const photo = label?.querySelector('img');
            if (photo) {
              choice.classList.add('ayaq-pdp__purchase-choice--color');
              const image = photo.cloneNode();
              image.removeAttribute('class');
              image.alt = '';
              choice.append(image);
            } else {
              choice.textContent =
                control.tagName === 'OPTION'
                  ? control.textContent.trim()
                  : control.value;
            }
            if (notify) {
              const icon = notify.querySelector('svg').cloneNode(true);
              icon.classList.add('ayaq-pdp__purchase-envelope');
              choice.append(icon);
            }
            choice.addEventListener(
              'click',
              () => {
                if (notify && !notify.hidden) {
                  this.section
                    .querySelector('ayaq-pdp-restock')
                    ?.open(notify, choice);
                  return;
                }
                if (group.tagName === 'SELECT') group.value = control.value;
                else control.checked = true;
                (group.tagName === 'SELECT' ? group : control).dispatchEvent(
                  new window.Event('change', { bubbles: true }),
                );
                this.scheduleSync();
              },
              { signal },
            );
            row.append(choice);
            return { control, label, notify, choice };
          });
        });
        this.submit.addEventListener(
          'click',
          () => {
            if (!this.submit.disabled) this.button.click();
          },
          { signal },
        );
        this.section.addEventListener('change', () => this.scheduleSync(), {
          signal,
        });
        document.addEventListener(
          'variant:change',
          (event) => {
            if (
              String(event.detail?.section) === this.section.dataset.section
            ) {
              this.scheduleSync();
            }
          },
          { signal },
        );
        this.mobile.addEventListener(
          'change',
          () => {
            this.updateMobileLayout();
            this.updateVisibility();
          },
          { signal },
        );
        this.mutations = new MutationObserver(() => this.scheduleSync());
        [this.button, this.price, this.picker, this.error]
          .filter(Boolean)
          .forEach((element) => {
            this.mutations.observe(element, {
              subtree: true,
              childList: true,
              characterData: true,
              attributes: true,
            });
          });
        this.intersection = new IntersectionObserver(() =>
          this.updateVisibility(),
        );
        this.intersection.observe(this.section);
        this.intersection.observe(this.button);
        this.resize = new ResizeObserver(() => {
          this.updateModelLayout();
          this.updateLayout();
          this.section.style.setProperty(
            '--pdp-purchase-height',
            `${this.getBoundingClientRect().height}px`,
          );
        });
        this.resize.observe(this);
        this.resize.observe(this.actions);
        if (this.sizeGroup) this.resize.observe(this.sizeGroup);
        document.fonts?.ready.then(() => {
          if (!signal.aborted) {
            this.updateModelLayout();
            this.updateLayout();
          }
        });
        this.updateMobileLayout();
        this.sync();
        this.updateVisibility();
      }

      updateMobileLayout() {
        this.layoutMoves.forEach(({ element, slot, marker }) => {
          if (this.mobile.matches) slot.append(element);
          else marker.after(element);
          if (element.matches('[data-ayaq-size-guide-trigger]')) {
            const label = element.querySelector('span');
            this.desktopGuideLabel ??= label.textContent;
            label.textContent = this.mobile.matches
              ? element.dataset.mobileLabel
              : this.desktopGuideLabel;
          }
        });
        this.section.classList.toggle(
          'ayaq-pdp--mobile-controls',
          this.mobile.matches,
        );
        this.updateModelLayout();
      }

      updateModelLayout() {
        const group = this.sizeGroup;
        if (!group) return;
        const summary = group.querySelector('.mannequin-info__summary');
        const fieldset = group.querySelector('fieldset');
        const values = group.querySelector('.ayaq-pdp__size-values');
        let fits = false;
        if (!this.mobile.matches && summary && fieldset && values) {
          // Sum text fragments to measure the full label even when it wraps.
          const range = document.createRange();
          range.selectNodeContents(summary);
          const textWidth = Array.from(range.getClientRects()).reduce(
            (width, rect) => width + rect.width,
            0,
          );
          const bubble = summary.closest('.mannequin-info__bubble');
          const style = window.getComputedStyle(bubble);
          const toggle = bubble.querySelector('.mannequin-info__toggle');
          const modelWidth = Math.ceil(
            textWidth +
              toggle.getBoundingClientRect().width +
              parseFloat(style.paddingLeft) +
              parseFloat(style.paddingRight),
          );
          fits = group.clientWidth >= fieldset.offsetWidth + modelWidth + 32;
          if (fits) {
            const colorGap =
              parseFloat(window.getComputedStyle(this.picker).columnGap) || 0;
            const spacing = `${Math.max(
              0,
              (group.clientWidth -
                fieldset.offsetWidth -
                modelWidth -
                colorGap) /
                2,
            )}px`;
            if (
              group.style.getPropertyValue('--pdp-model-spacing') !== spacing
            ) {
              group.style.setProperty('--pdp-model-spacing', spacing);
            }
            const offset = Math.max(
              0,
              values.getBoundingClientRect().bottom -
                group.getBoundingClientRect().top -
                bubble.getBoundingClientRect().height,
            );
            const value = `${offset}px`;
            if (group.style.getPropertyValue('--pdp-model-offset') !== value) {
              group.style.setProperty('--pdp-model-offset', value);
            }
          }
        }
        group.classList.toggle('ayaq-pdp__option-group--model-aside', fits);
      }

      scheduleSync() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.sync();
        });
      }

      sync() {
        this.groups.forEach((group, index) => {
          this.choices[index].forEach(({ control, label, notify, choice }) => {
            choice.hidden =
              control.hidden ||
              control.disabled ||
              label?.hidden ||
              label?.style.display === 'none';
            const restock = Boolean(notify && !notify.hidden);
            const selected =
              group.tagName === 'SELECT'
                ? group.value === control.value
                : control.checked;
            choice.classList.toggle(
              'ayaq-pdp__purchase-choice--restock',
              restock,
            );
            choice.setAttribute('aria-pressed', String(selected));
            choice.setAttribute(
              'aria-label',
              restock ? notify.getAttribute('aria-label') : control.value,
            );
            choice.title = restock ? notify.title : control.value;
            if (restock) choice.setAttribute('aria-haspopup', 'dialog');
            else choice.removeAttribute('aria-haspopup');
          });
        });
        this.choices.forEach((choices, index) => {
          const count = choices.filter(({ choice }) => !choice.hidden).length;
          this.rows[index].hidden = count === 0;
          this.rows[index].style.setProperty(
            '--pdp-choice-count',
            String(count),
          );
        });
        const amount = this.price?.querySelector(
          '.price--on-sale .price__sale .price-item--sale, .price:not(.price--on-sale) .price__regular .price-item--regular',
        );
        if (amount) {
          this.querySelector('[data-pdp-purchase-price]').textContent =
            amount.textContent.trim();
        }
        this.submit.disabled =
          this.button.disabled ||
          this.button.getAttribute('aria-disabled') === 'true';
        const submitLabel =
          this.button.querySelector('.add-to-cart-label')?.textContent.trim() ||
          this.button.textContent.trim();
        this.submit.querySelector('[data-pdp-submit-label]').textContent =
          submitLabel;
        this.submit.setAttribute('aria-label', submitLabel);
        this.submit.setAttribute(
          'aria-busy',
          String(this.button.classList.contains('loading')),
        );
        const errorText =
          this.error && !this.error.hidden
            ? this.error
                .querySelector('.product-form__error-message')
                ?.textContent.trim()
            : '';
        const feedback = this.querySelector('[data-pdp-purchase-error]');
        feedback.hidden = !errorText;
        feedback.textContent = errorText || '';
        this.updateModelLayout();
        this.updateLayout();
      }

      updateLayout() {
        if (this.hidden) return;
        const available = this.content.clientWidth;
        const gap =
          parseFloat(window.getComputedStyle(this.content).columnGap) || 0;
        const widths = { colors: 0, options: 0 };
        const counts = { colors: 0, options: 0 };
        this.rows.forEach((row, index) => {
          const count = this.choices[index].filter(
            ({ choice }) => !choice.hidden,
          ).length;
          const key =
            row.parentElement === this.colorOptions ? 'colors' : 'options';
          if (count) {
            if (counts[key]) widths[key] += 12;
            widths[key] += count * 44 + Math.max(0, count - 1) * 6;
            counts[key] += count;
          }
        });
        const titleWidth = this.productTitle.getBoundingClientRect().width;
        const priceWidth = this.priceLabel.getBoundingClientRect().width;
        const labelWidth = this.submit
          .querySelector('[data-pdp-submit-label]')
          .getBoundingClientRect().width;
        const compactButton = Math.max(140, Math.ceil(labelWidth) + 24);
        const fullButton = Math.max(
          compactButton,
          Math.min(320, available * 0.22),
        );
        const rightWidth = fullButton + priceWidth + 16;
        const leftWidth = widths.colors + (counts.colors ? 16 : 0) + titleWidth;
        const fitsTitle =
          2 * Math.max(leftWidth, rightWidth) + widths.options + 2 * gap <=
          available;
        let layout = 'wide';
        if (
          !fitsTitle &&
          2 * Math.max(widths.colors, rightWidth) + widths.options + 2 * gap >
            available
        ) {
          layout =
            widths.colors +
              widths.options +
              compactButton +
              priceWidth +
              16 +
              2 * gap <=
            available
              ? 'compact'
              : 'stacked';
        }
        this.toggleAttribute('data-title-visible', fitsTitle);
        this.toggleAttribute('data-no-colors', !counts.colors);
        this.toggleAttribute('data-no-options', !counts.options);
        this.dataset.layout = layout;
        this.style.setProperty(
          '--pdp-cart-width',
          `${layout === 'wide' ? fullButton : compactButton}px`,
        );
        this.style.setProperty(
          '--pdp-color-track',
          `${Math.max(1, counts.colors)}fr`,
        );
        this.style.setProperty(
          '--pdp-option-track',
          `${Math.max(1, counts.options)}fr`,
        );
      }

      updateVisibility() {
        const bounds = this.section.getBoundingClientRect();
        this.hidden = this.mobile.matches
          ? bounds.bottom <= 0 || bounds.top >= window.innerHeight
          : this.button.getBoundingClientRect().bottom >= 0 ||
            bounds.bottom <= 80;
        this.updateLayout();
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.controller = null;
        this.mutations?.disconnect();
        this.intersection?.disconnect();
        this.resize?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = null;
        this.layoutMoves?.forEach(({ element, marker }) => {
          marker.after(element);
          marker.remove();
        });
        this.sizeGroup?.classList.remove('ayaq-pdp__option-group--model-aside');
        this.sizeGroup?.style.removeProperty('--pdp-model-offset');
        this.sizeGroup?.style.removeProperty('--pdp-model-spacing');
        this.section?.classList.remove('ayaq-pdp--mobile-controls');
        this.section?.style.removeProperty('--pdp-purchase-height');
      }
    },
  );
}
